import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import { memo, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { CRUISE_LINE_INFO, DEFAULT_CRUISE_LINE_FINE_PRINT_LINK } from 'constants/cruise'

interface Props {
  cruiseLine: App.CruiseLine
  bundleTermsAndConditions?: string | null
  compact?: boolean
}

function formatBundleTermsAndConditions(termsAndConditions: string): string {
  return termsAndConditions
    .replaceAll('\r\n', '\n')
    .replaceAll('\n', '  \n')
}

function CruiseFinePrint({ cruiseLine, bundleTermsAndConditions, compact = false }: Props) {
  const intl = useIntl()
  const { code: cruiseLineCode, name: cruiseLineName } = cruiseLine
  const formattedBundleTermsAndConditions = bundleTermsAndConditions?.trim()
    ? formatBundleTermsAndConditions(bundleTermsAndConditions.trim())
    : null

  const termsAndConditionsLink = useMemo(() => {
    if (cruiseLineCode && cruiseLineCode in CRUISE_LINE_INFO) {
      return CRUISE_LINE_INFO[cruiseLineCode as keyof typeof CRUISE_LINE_INFO].finePrintLink
    }

    return DEFAULT_CRUISE_LINE_FINE_PRINT_LINK
  }, [cruiseLineCode])
  const shouldShowCruiseLineTermsLink = termsAndConditionsLink !== DEFAULT_CRUISE_LINE_FINE_PRINT_LINK

  const heading = (
    <Heading
      align={compact ? undefined : 'center'}
      variant={compact ? 'heading3' : 'heading2'}
    >
      <FormattedMessage
        id="5yHiib-00mPwk5MXY70i"
        defaultMessage="Booking Terms & Conditions"
        description="Heading for cruise booking terms and conditions section"
      />
    </Heading>
  )

  const content = (
    <Group direction="vertical" gap={16}>
      {!!formattedBundleTermsAndConditions &&
        <MarkdownRender
          content={formattedBundleTermsAndConditions}
          fontSize="medium"
          type="compact"
        />}
      <BodyText variant="medium">
        <FormattedMessage
          id="sS-SRQzrF0K56MW5tDJo"
          defaultMessage="By completing your booking, you agree to both the Luxury Escapes Terms & Conditions and the terms and conditions of the relevant cruise line operator."
          description="Fine print notice explaining the customer agrees to Luxury Escapes and cruise line terms"
        />
      </BodyText>
      <BodyText variant="medium">
        <FormattedMessage
          id="flsGI9QolPalpt6-ufkw"
          defaultMessage="Luxury Escapes acts as an intermediary in arranging your cruise booking. Cruise bookings, including cancellation policies, fare rules, booking conditions and operational requirements, are subject to the applicable cruise line’s terms and conditions, which may vary depending on the cruise line, fare type, cabin category and package selected."
          description="Fine print notice explaining the cruise line terms that govern cruise booking requirements"
        />
      </BodyText>
      <BodyText variant="medium">
        <FormattedMessage
          id="Z7kaz0IX-Ilu2hQvK_SQ"
          defaultMessage="Luxury Escapes may also charge its own Cruise Service Fees for certain booking amendments and cancellations. These fees are separate from, and in addition to, any fees or penalties charged by the cruise line or other travel suppliers. Please refer to the Luxury Escapes Terms & Conditions for full details."
          description="Fine print notice explaining separate Luxury Escapes fees for changes and cancellations"
        />
      </BodyText>
      <BodyText variant="medium">
        <FormattedMessage
          id="pZU3gg2dqrIElGCpZTKJ"
          defaultMessage="It is important that you review and accept both the Luxury Escapes Terms & Conditions and the relevant cruise line’s Terms & Conditions before finalising your purchase. These documents contain important information regarding booking conditions, luggage, boarding requirements, travel documentation, amendments, cancellations and refunds."
          description="Fine print notice requesting review of both sets of terms before the cruise purchase"
        />
      </BodyText>
      <TextLink size="medium" to="/booking-policies#cruises" target="_blank" weight="bold" underline>
        {intl.formatMessage({
          id: 'dvveRNF7ZeuCcVQaKGRN',
          defaultMessage: 'Click here to view the Luxury Escapes Cruise Terms & Conditions',
          description: 'Link from cruise offer fine print to the Luxury Escapes cruise booking policies section',
        })}
      </TextLink>
      {shouldShowCruiseLineTermsLink &&
        <TextLink size="medium" href={termsAndConditionsLink} target="_blank" weight="bold" underline>
          {intl.formatMessage(
            {
              id: '0o8Oh55fqOlkHPeBlan9',
              defaultMessage: 'Click here to view the {cruiseLineName} Terms & Conditions',
              description: 'Link from cruise offer fine print to the relevant cruise line terms and conditions',
            },
            { cruiseLineName },
          )}
        </TextLink>}
    </Group>
  )

  if (compact) {
    return (
      <Group direction="vertical" gap={24}>
        {heading}
        {content}
      </Group>
    )
  }

  return (
    <Pane padding="32 32">
      <Group direction="vertical" gap={48}>
        {heading}
        <Pane>
          <LayoutContainer>
            {content}
          </LayoutContainer>
        </Pane>
      </Group>
    </Pane>
  )
}

export default memo(CruiseFinePrint)
